/** 新商品画像キャッシュの有効期限（12時間） */
const CACHE_TTL_MS = 12 * 60 * 60 * 1000;
/** 失敗時の最大リトライ回数（初回以外） */
const MAX_RETRIES = 3;
/** リトライ待機の基準時間（ms） */
const RETRY_BASE_MS = 500;

/** @type {Map<string, { src: string|null, noImage: boolean, expiresAt: number }>} */
const cache = new Map();
/** @type {Map<string, Promise<{ src: string|null, noImage: boolean, fromCache?: boolean, error?: boolean }>>} */
const inFlight = new Map();

function cacheKey(itemId) {
    return String(itemId);
}

function getCachedEntry(itemId) {
    const entry = cache.get(cacheKey(itemId));
    if (!entry) return undefined;
    if (Date.now() > entry.expiresAt) {
        cache.delete(cacheKey(itemId));
        return undefined;
    }
    return entry;
}

function setCachedEntry(itemId, { src, noImage }) {
    cache.set(cacheKey(itemId), {
        src: src ?? null,
        noImage: Boolean(noImage),
        expiresAt: Date.now() + CACHE_TTL_MS,
    });
}

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function shouldRetry(status, attempt) {
    if (attempt >= MAX_RETRIES) return false;
    if (status == null) return true;
    return status === 429 || status >= 500;
}

/** ログアウト時にキャッシュを破棄する */
export function clearNewItemPhotoCache() {
    cache.clear();
    inFlight.clear();
}

/** 指定 item のキャッシュを破棄する（画像更新時） */
export function invalidateNewItemPhotoCache(itemId) {
    if (itemId == null) return;
    const key = cacheKey(itemId);
    cache.delete(key);
    inFlight.delete(key);
}

/** 同期的にキャッシュを参照する（初回描画のちらつき防止） */
export function peekNewItemPhotoCache(itemId) {
    const entry = getCachedEntry(itemId);
    if (entry === undefined) {
        return { resolved: false, src: null, noImage: false };
    }
    return {
        resolved: true,
        src: entry.src,
        noImage: entry.noImage,
    };
}

/**
 * 新商品画像を取得する（12時間キャッシュ・失敗時リトライ付き）
 * @returns {{ src: string|null, noImage: boolean, fromCache?: boolean, error?: boolean }}
 */
export async function fetchNewItemPhoto(
    itemId,
    { apiBaseUrl = "/api3008", getHeaders, bypassCache = false, signal } = {}
) {
    if (!itemId) {
        return { src: null, noImage: true };
    }

    const key = cacheKey(itemId);

    if (!bypassCache) {
        const cached = getCachedEntry(itemId);
        if (cached !== undefined) {
            return { src: cached.src, noImage: cached.noImage, fromCache: true };
        }
    }

    const existing = inFlight.get(key);
    if (existing) {
        const result = await existing;
        if (signal?.aborted) {
            throw new DOMException("Aborted", "AbortError");
        }
        return result;
    }

    const promise = (async () => {
        const headers = getHeaders ? getHeaders() : {};

        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
            try {
                const res = await fetch(
                    `${apiBaseUrl}/get_new_item_photo_s3?item_id=${encodeURIComponent(itemId)}`,
                    { headers }
                );

                if (!res.ok) {
                    if (shouldRetry(res.status, attempt)) {
                        await sleep(RETRY_BASE_MS * 2 ** attempt);
                        continue;
                    }
                    return { src: null, noImage: false, error: true };
                }

                const json = await res.json();
                if (!json.image?.data) {
                    setCachedEntry(itemId, { src: null, noImage: true });
                    return { src: null, noImage: true };
                }

                const ct = json.image.content_type || "image/jpeg";
                const src = `data:${ct};base64,${json.image.data}`;
                setCachedEntry(itemId, { src, noImage: false });
                return { src, noImage: false };
            } catch (e) {
                if (shouldRetry(null, attempt)) {
                    await sleep(RETRY_BASE_MS * 2 ** attempt);
                    continue;
                }
                return { src: null, noImage: false, error: true };
            }
        }

        return { src: null, noImage: false, error: true };
    })();

    inFlight.set(key, promise);
    try {
        const result = await promise;
        if (signal?.aborted) {
            throw new DOMException("Aborted", "AbortError");
        }
        return result;
    } finally {
        if (inFlight.get(key) === promise) {
            inFlight.delete(key);
        }
    }
}
